import { useState, useEffect, useMemo } from 'react';

export default function useDevices() {
    const [devices, setDevices] = useState([]);

    useEffect(() => {
        const getDevices = () => navigator.mediaDevices.enumerateDevices().then(devices => setDevices(devices));
        navigator.mediaDevices.addEventListener('devicechange', getDevices);
        getDevices();

        // Also re-enumerate when mic/camera permissions change (browser settings toggle).
        // The 'devicechange' event does NOT fire for permission changes in most browsers.
        const permissionStatuses = [];
        const onPermissionChange = () => getDevices();
        ['microphone', 'camera'].forEach(name => {
            navigator.permissions?.query({ name }).then(status => {
                permissionStatuses.push(status);
                status.addEventListener('change', onPermissionChange);
            }).catch(() => {});
        });

        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', getDevices);
            permissionStatuses.forEach(s => s.removeEventListener('change', onPermissionChange));
        };
    }, []);

    // Memoize filtered arrays to prevent unnecessary re-renders and callback recreations
    const audioInputDevices = useMemo(
        () => devices.filter(device => device.kind === 'audioinput'),
        [devices]
    );
    const videoInputDevices = useMemo(
        () => devices.filter(device => device.kind === 'videoinput'),
        [devices]
    );
    const audioOutputDevices = useMemo(
        () => devices.filter(device => device.kind === 'audiooutput'),
        [devices]
    );

    return {
        audioInputDevices,
        videoInputDevices,
        audioOutputDevices,
        hasAudioInputDevices: audioInputDevices.length > 0,
        hasVideoInputDevices: videoInputDevices.length > 0,
        hasAudioOutputDevices: audioOutputDevices.length > 0,
    };
}
